import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@clerk/react';
import { Dialog, DialogPanel, DialogTitle } from '@headlessui/react';
import { useApiClient } from '../../hooks/use-api-client.js';
import { pushToast } from '../app/toast-store.js';
import { useGlobalErrorShake } from '../../components/ui/error-shake/error-shake-context.js';

type GdprStatus = 'active' | 'pendingDeletion' | 'restricted';

interface StatusResponse {
  status: GdprStatus;
  scheduledHardDelete?: string;
  restrictedAt?: string;
}

export function GdprSection() {
  const { t, i18n } = useTranslation('settings');
  const { fetchApi } = useApiClient();
  const { signOut } = useAuth();
  const shake = useGlobalErrorShake();

  const [accountStatus, setAccountStatus] = useState<StatusResponse | null>(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [restrictLoading, setRestrictLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetchApi('/v1/gdpr/status');
      if (res.ok) {
        const body = (await res.json()) as { data?: StatusResponse };
        if (body.data) setAccountStatus(body.data);
      }
    } catch {
      // Non-fatal — fall back to active
    }
  }, [fetchApi]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  async function handleExport() {
    setExportLoading(true);
    try {
      const res = await fetchApi('/v1/gdpr/export', { method: 'POST' });
      if (!res.ok) {
        pushToast({ tone: 'error', title: t('gdpr.export.error') });
        shake();
        return;
      }

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'agentbrush-data-export.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      pushToast({ tone: 'success', title: t('gdpr.export.success') });
    } catch {
      pushToast({ tone: 'error', title: t('gdpr.export.error') });
      shake();
    } finally {
      setExportLoading(false);
    }
  }

  async function handleDelete() {
    setDeleteLoading(true);
    try {
      const res = await fetchApi('/v1/gdpr/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(deleteReason ? { reason: deleteReason } : {}),
      });
      if (!res.ok) {
        pushToast({ tone: 'error', title: t('gdpr.delete.error') });
        shake();
        return;
      }

      pushToast({ tone: 'success', title: t('gdpr.delete.success') });
      setDeleteDialogOpen(false);

      try {
        await signOut({ redirectUrl: '/' });
      } catch {
        // Deletion succeeded but sign-out failed — redirect manually
        window.location.href = '/';
      }
      return;
    } catch {
      pushToast({ tone: 'error', title: t('gdpr.delete.error') });
      shake();
    } finally {
      setDeleteLoading(false);
    }
  }

  async function handleCancelDeletion() {
    setCancelLoading(true);
    try {
      const res = await fetchApi('/v1/gdpr/cancel-deletion', { method: 'POST' });
      if (!res.ok) {
        pushToast({ tone: 'error', title: t('gdpr.pending.cancelError') });
        shake();
        return;
      }

      pushToast({ tone: 'success', title: t('gdpr.pending.cancelSuccess') });
      await fetchStatus();
    } catch {
      pushToast({ tone: 'error', title: t('gdpr.pending.cancelError') });
      shake();
    } finally {
      setCancelLoading(false);
    }
  }

  async function handleRestrict() {
    setRestrictLoading(true);
    try {
      const res = await fetchApi('/v1/gdpr/restrict', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'restrict' }),
      });
      if (!res.ok) {
        pushToast({ tone: 'error', title: t('gdpr.restrict.error') });
        shake();
        return;
      }

      await fetchStatus();
    } catch {
      pushToast({ tone: 'error', title: t('gdpr.restrict.error') });
      shake();
    } finally {
      setRestrictLoading(false);
    }
  }

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(i18n.language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <section aria-labelledby="settings-gdpr-heading" className="mb-2">
      <h2 id="settings-gdpr-heading" className="text-h2 font-semibold text-primary mb-4">
        {t('gdpr.heading')}
      </h2>
      <p className="text-body text-secondary mb-4">{t('gdpr.description')}</p>

      {/* Pending Deletion Banner */}
      {accountStatus?.status === 'pendingDeletion' && accountStatus.scheduledHardDelete && (
        <div role="alert" className="mb-4 rounded border border-red-500/30 bg-red-500/10 px-4 py-3">
          <p className="text-sm text-red-300">
            {t('gdpr.pending.banner', { date: formatDate(accountStatus.scheduledHardDelete) })}
          </p>
          <button
            type="button"
            onClick={handleCancelDeletion}
            disabled={cancelLoading}
            data-testid="gdpr-cancel-deletion-button"
            className="btn-press mt-2 rounded px-3 py-1.5 text-sm font-medium bg-white/10 text-primary hover:bg-white/15 transition-colors cursor-pointer disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          >
            {cancelLoading ? t('billing.loading') : t('gdpr.pending.cancelButton')}
          </button>
        </div>
      )}

      {/* Restricted Banner */}
      {accountStatus?.status === 'restricted' && (
        <div
          role="alert"
          className="mb-4 rounded border border-amber-500/30 bg-amber-500/10 px-4 py-3"
        >
          <p className="text-sm text-amber-300">{t('gdpr.restrict.active')}</p>
          <p className="text-xs text-secondary mt-1">{t('gdpr.restrict.reactivate')}</p>
        </div>
      )}

      <div className="flex flex-wrap gap-3">
        {/* Data Export */}
        <button
          type="button"
          onClick={handleExport}
          disabled={exportLoading}
          data-testid="gdpr-export-button"
          className="rounded px-4 py-2 text-sm font-medium bg-white/10 text-primary hover:bg-white/15 transition-colors cursor-pointer disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
        >
          {exportLoading ? t('gdpr.export.loading') : t('gdpr.export.button')}
        </button>

        {/* Account Restriction (only show if active) */}
        {accountStatus?.status === 'active' && (
          <button
            type="button"
            onClick={handleRestrict}
            disabled={restrictLoading}
            data-testid="gdpr-restrict-button"
            className="rounded px-4 py-2 text-sm font-medium bg-white/10 text-secondary hover:bg-white/15 transition-colors cursor-pointer disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          >
            {restrictLoading ? t('billing.loading') : t('gdpr.restrict.button')}
          </button>
        )}

        {/* Account Deletion (only show if active or restricted, not pending) */}
        {accountStatus?.status !== 'pendingDeletion' && (
          <button
            type="button"
            onClick={() => setDeleteDialogOpen(true)}
            data-testid="gdpr-delete-button"
            className="rounded px-4 py-2 text-sm font-medium text-red-400 bg-transparent hover:bg-red-500/10 transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          >
            {t('gdpr.delete.button')}
          </button>
        )}
      </div>

      {/* Deletion Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/60" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center">
          <DialogPanel className="relative w-full max-w-md rounded-lg border border-white/10 bg-[#1a1a2e] p-6 shadow-xl backdrop-blur-xl">
            <DialogTitle className="text-h3 font-semibold text-primary">
              {t('gdpr.delete.confirmTitle')}
            </DialogTitle>

            <p className="text-body text-secondary mt-3">{t('gdpr.delete.confirmBody')}</p>

            <label className="block mt-4">
              <span className="text-sm text-secondary">{t('gdpr.delete.reasonLabel')}</span>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                maxLength={500}
                rows={3}
                className="mt-1 w-full rounded border border-white/10 bg-white/5 px-3 py-2 text-sm text-primary placeholder:text-secondary/50 outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400 resize-none"
              />
            </label>

            <div className="flex gap-3 mt-6 justify-end">
              <button
                type="button"
                onClick={() => setDeleteDialogOpen(false)}
                disabled={deleteLoading}
                className="rounded px-4 py-2 text-sm font-medium bg-white/10 text-primary hover:bg-white/15 transition-colors cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
              >
                {t('gdpr.delete.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteLoading}
                data-testid="gdpr-confirm-delete-button"
                className="rounded px-4 py-2 text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors cursor-pointer disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
              >
                {deleteLoading ? t('billing.loading') : t('gdpr.delete.confirm')}
              </button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </section>
  );
}
